import { ChatThreadItem } from '@euler/model/chat';

/** Avatar glyph for group threads — WeChat-style single character. */
export const GROUP_AVATAR_CHAR = '群';

export function isGroupThread(thread: ChatThreadItem): boolean {
  return thread.room?.type === 'group';
}

/**
 * Whether a thread is pinned to the top. `pinned_at` is a non-null int
 * sentinel (0 = unpinned, >0 = pinned) the wire may carry as number or
 * string. Single source of truth so the sort, the row shading, and the
 * pinned-section divider can't drift on what "pinned" means.
 */
export function chatThreadIsPinned(thread: ChatThreadItem): boolean {
  return !!thread.pinnedAt && Number(thread.pinnedAt) > 0;
}

/**
 * Strip the viewer's own name from an auto-composed group title
 * (`A、B、C`). The backend stamps `default_title` with the first N member
 * names (everyone, incl. self); each viewer hides their own name at
 * render — the "exclude thread owner" rule, done FE-side per the design.
 */
export function stripSelfFromGroupTitle(
  defaultTitle: string | null | undefined,
  selfName: string | null | undefined,
): string {
  if (!defaultTitle) return '';
  const self = selfName?.trim();
  const parts = defaultTitle
    .split('、')
    .map(s => s.trim())
    .filter(Boolean);
  const kept = self ? parts.filter(p => p !== self) : parts;
  // If stripping self emptied the list (1-person group), keep the raw set.
  return (kept.length > 0 ? kept : parts).join('、');
}

/**
 * Clamp an auto-composed group title (`A、B、C…`) to the first `maxNames`
 * members, appending an ellipsis when more were dropped. Keeps the
 * conversation header short for large rooms so the trailing member-count
 * stays visible and the title doesn't collide with the header-right
 * actions. A short custom (owner-set) title has ≤ `maxNames` `、`-segments
 * and is returned unchanged.
 */
export function clampGroupTitleNames(title: string, maxNames = 3): string {
  const parts = title
    .split('、')
    .map(s => s.trim())
    .filter(Boolean);
  if (parts.length <= maxNames) return parts.join('、');
  return parts.slice(0, maxNames).join('、') + '…';
}

/**
 * Display title for an inbox row / conversation header.
 *  - private: the peer's snapshot name (no member/contact load).
 *  - group:   the owner-set `title`, else the self-stripped auto title.
 */
export function chatThreadTitle(
  thread: ChatThreadItem,
  selfName?: string | null,
): string {
  if (isGroupThread(thread)) {
    const custom = thread.room?.title?.trim();
    if (custom) return custom;
    return (
      stripSelfFromGroupTitle(thread.room?.defaultTitle, selfName) || '群聊'
    );
  }
  return (
    thread.peer?.name?.trim() ||
    thread.latestMessage?.authorName?.trim() ||
    '聊天'
  );
}

/**
 * Avatar source for an inbox row. An order-internal room uses the
 * vehicle brand logo; other group threads render the `群` glyph (no
 * image); private threads use the peer's avatar snapshot.
 */
export function chatThreadAvatar(thread: ChatThreadItem): {
  uri?: string;
  name: string;
} {
  if (isGroupThread(thread)) {
    const brandLogo = thread.room?.orderSnapshot?.vehicleBrandLogo;
    return {
      uri: brandLogo ?? undefined,
      name: GROUP_AVATAR_CHAR,
    };
  }
  return {
    uri: thread.peer?.avatarUrl ?? undefined,
    name: thread.peer?.name?.trim() || '聊天',
  };
}
